import type { TeamId } from "@arcade-hockey/shared";
import { type SkaterRole, type TacticId, tuning } from "@arcade-hockey/sim";
import type { Situation } from "../situation.js";

// Writes the role's home target into `out` for the given situation. Pure
// function of arguments plus tuning; zero allocations. Caller owns the
// output Vec2.
//
// Conceptual model (the role × situation home-target table):
//   - attackDir +1 for home (attacks +x), -1 for away.
//   - laneY is authored as world-y on the "own-left" side (positive); HOME
//     LW and AWAY LW both land at +y world, so a player always owns the
//     same physical side of the ice from their own goalie's POV.
//   - x sits ahead of the puck for offensive jobs, behind for defensive,
//     clamped between own-goal backstop and either the role's
//     maxForwardFromOwnGoal cap or the global forwardCapFromEnemyGoal.
//   - y sits in the role's lane with a 0..1 lateral pull toward puck.y.
//
// Slice 2: same five parameters, but each (role, situation) cell now has
// its own set in tuning.positioning. Slice 4 will key the table by
// per-team tactic id so a hidden hotkey can swap between schemes.

type Vec2Out = { x: number; y: number };

function clamp(v: number, lo: number, hi: number): number {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

export function homePosition(
  out: Vec2Out,
  role: SkaterRole,
  teamId: TeamId,
  tactic: TacticId,
  situation: Situation,
  puckX: number,
  puckY: number,
): void {
  const cfg = tuning.positioning[role][situation];
  const halfLength = tuning.rink.length / 2;
  const attackDir = teamId === "home" ? 1 : -1;
  const ownGoalX = -attackDir * halfLength;
  const enemyGoalX = attackDir * halfLength;

  // Tactic modifiers — applied to the cell's parameters before clamping.
  // Scalars (not data overrides) so the data model stays one table; the
  // shape of each tactic is the bias it places on top of the balanced
  // numbers. Zero allocations.
  //   - balanced: identity
  //   - trap: pull everyone back. Forwards forecheck less; D less likely
  //     to pinch. Strong defensive shell for protecting a lead.
  //   - aggressive: push everyone up. Forwards forecheck deeper; D
  //     activate further. Trades coverage for offensive pressure.
  let forwardOffsetMul = 1;
  let maxFwdMul = 1;
  if (tactic === "trap") {
    forwardOffsetMul = 0.6;
    maxFwdMul = 0.8;
  } else if (tactic === "aggressive") {
    forwardOffsetMul = 1.3;
    maxFwdMul = 1.15;
  }

  // Forward axis: roles sit `forwardOffset` meters from the puck in the
  // direction of attack. Forwards have positive offset (ahead of puck) in
  // offensive situations and small negative offsets when defending. D
  // generally have larger negative offsets (sit behind play).
  let x = puckX + attackDir * cfg.forwardOffset * forwardOffsetMul;

  // Backstop: never retreat through the back of your own net.
  const ownGoalSideCap = ownGoalX + attackDir * cfg.backstopFromOwnGoal;

  // Forward caps: most-restrictive of (global enemy-goal cap, per-cell
  // max-from-own-goal). Tactic-scaled maxFwd lets trap pull everyone
  // back further; aggressive lets D activate further up.
  const enemySideGlobalCap = enemyGoalX - attackDir * tuning.positioning.forwardCapFromEnemyGoal;
  const fromOwnGoalCap = ownGoalX + attackDir * cfg.maxForwardFromOwnGoal * maxFwdMul;
  const forwardCapX =
    attackDir > 0
      ? Math.min(enemySideGlobalCap, fromOwnGoalCap)
      : Math.max(enemySideGlobalCap, fromOwnGoalCap);

  const xLow = attackDir > 0 ? ownGoalSideCap : forwardCapX;
  const xHigh = attackDir > 0 ? forwardCapX : ownGoalSideCap;
  x = clamp(x, xLow, xHigh);

  // Lateral: hold the role's lane, optionally drifting toward puck.y.
  // laneY is already in world coordinates so no mirror is needed.
  const laneCenter = cfg.laneY;
  let y = laneCenter + cfg.lateralPullToPuck * (puckY - laneCenter);

  // Strong-side / weak-side D split in defensive-zone defense. Real
  // hockey: the D on the puck side takes the corner / boards; the D on
  // the far side guards the slot / net front. Without this override
  // both D pile on the net and the puck-side wall is uncontested. Only
  // applies when defending in our own zone — point/blue-line situations
  // already differentiate naturally via lane discipline.
  if (situation === "DefZoneTheirPuck" && (role === "LD" || role === "RD")) {
    const onPuckSide = laneCenter * puckY > 0;
    if (onPuckSide) {
      // Strong-side D: take the corner / boards on the puck side.
      // Aim for a y between the puck and the boards (laneCenter side).
      y = puckY + (laneCenter > 0 ? 2 : -2);
    } else {
      // Weak-side D: own the slot / net front.
      y = 0;
    }
  }

  out.x = x;
  out.y = y;
}
